// AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import NewUserAccountForm from './NewUserAccountForm.jsx';
import RecordPaymentForm from './RecordPaymentForm.jsx';
import CustomerBalances from './CustomerBalances.jsx';
import ReportsDashboard from './ReportsDashboard.jsx';
import CompanyCenter from './CompanyCenter.jsx';
import Financial from './Financial.jsx';
import Expenses from './Expenses.jsx';

const API_BASE_URL = 'https://kigaawater.inkenya.co.ke/api';

function AdminDashboard() {
  // Get user role from localStorage (set during login)
  const userRole = parseInt(localStorage.getItem('userRole'), 10) || 4; // Default to Field Agent if missing

  const [view, setView] = useState(userRole === 4 ? 'payment' : 'account'); // Field Agent always lands on Record Payment
  const [notifications, setNotifications] = useState([]); // Persistent alert log array

  const userFullName = localStorage.getItem('employeeFullName') || 'System User';

  // Poll for new transaction events every 7 seconds
  useEffect(() => {
    const fetchLiveUpdates = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/mpesa-notifications`);
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          // Append new incoming entries to existing dashboard alerts without clearing them automatically
          setNotifications(prev => [...response.data, ...prev]);
        }
      } catch (err) {
        console.error('M-Pesa log synchronization failed:', err.message);
      }
    };

    fetchLiveUpdates();
    const syncTimer = setInterval(fetchLiveUpdates, 7000);
    return () => clearInterval(syncTimer);
  }, []);

  // Remove individual notification from layout state when dismissed
  const handleDismissAlert = (targetId) => {
    setNotifications(prev => prev.filter(item => item.id !== targetId));
  };

  const renderContent = () => {
    switch (view) {
      case 'account':
        return <NewUserAccountForm />;
      case 'payment':
        return <RecordPaymentForm />;
      case 'customer-balances':
        return <CustomerBalances />;
      case 'reports':
        return <ReportsDashboard />;
      case 'company-center':
        return <CompanyCenter />;
      case 'financial':
        return <Financial />;
      case 'expenses': 
        return <Expenses />;
      default:
        return <NewUserAccountForm />;
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Water Society Admin Portal</h1>
        <p style={styles.loggedInAs}>Logged in as: {userFullName}</p>
        <nav style={styles.nav}>
          {/* 3.1 - New Account Creation */}
          {(userRole === 1 || userRole === 2 || userRole === 3) && (
            <button
              onClick={() => setView('account')}
              style={view === 'account' ? styles.activeButton : styles.navButton}
            >
              New Account Creation (3.1)
            </button>
          )}

          {/* 3.2 - Record Payment */}
          {(userRole === 1 || userRole === 2 || userRole === 3 || userRole === 4) && (
            <button
              onClick={() => setView('payment')}
              style={view === 'payment' ? styles.activeButton : styles.navButton}
            >
              Record Payment (3.2 - Cash/Credit)
            </button>
          )}

          {/* 3.3 - Customer Balances */}
          {(userRole === 1 || userRole === 2 || userRole === 3) && (
            <button
              onClick={() => setView('customer-balances')}
              style={view === 'customer-balances' ? styles.activeButton : styles.navButton}
            >
              Customer Balances (3.3)
            </button>
          )}

          {/* 3.4 - Reports */}
          {(userRole === 1 || userRole === 2) && (
            <button
              onClick={() => setView('reports')}
              style={view === 'reports' ? styles.activeButton : styles.navButton}
            >
              Reports (3.4)
            </button>
          )}

          {/* 3.5 - Company Center */}
          {(userRole === 1) && (
            <button
              onClick={() => setView('company-center')}
              style={view === 'company-center' ? styles.activeButton : styles.navButton}
            >
              Company Center (3.5)
            </button>
          )}

          {(userRole === 1 || userRole === 2) && (
            <button
              onClick={() => setView('financial')}
              style={view === 'financial' ? styles.activeButton : styles.navButton}
            >
              Financial Statements (3.6)
            </button>
          )}

          {/* 3.7 - Expenses and Payroll */}
          {(userRole === 1 || userRole === 2) && (
            <button
              onClick={() => setView('expenses')}
              style={view === 'expenses' ? styles.activeButton : styles.navButton}
            >
              Expenses and Payroll (3.7)
            </button>
          )}

          {/* SIGN OUT BUTTON */}
          <button
            onClick={() => {
              localStorage.removeItem('authToken');
              localStorage.removeItem('userRole');
              localStorage.removeItem('userId');
              window.location.href = '/';
            }}
            style={styles.logoutButton}
          >
            Sign Out
          </button>
        </nav>
      </header>

      <main style={styles.mainContent}>
        {/* --- LIVE M-PESA MONITORING PANELS --- */}
        {notifications.length > 0 && (
          <div style={styles.alertPanelWrapper}>
            <div style={styles.panelHeadingRow}>
              <h4 style={{ margin: 0, fontSize: '15px', color: '#333', fontWeight: '700' }}>
                📱 Live M-Pesa Callback Status Stream ({notifications.length})
              </h4>
              <button onClick={() => setNotifications([])} style={styles.clearAllTextBtn}>
                Clear All Logs
              </button>
            </div>
            <div style={styles.alertItemsContainer}>
              {notifications.map((item) => {
                const isSuccess = item.status === 'success';
                return (
                  <div 
                    key={item.id} 
                    style={{
                      ...styles.alertCard,
                      backgroundColor: isSuccess ? '#e8f5e9' : '#ffebee',
                      borderLeft: isSuccess ? '5px solid #2e7d32' : '5px solid #c62828'
                    }}
                  >
                    <span style={{ color: isSuccess ? '#1b5e20' : '#b71c1c', fontSize: '13.5px', lineHeight: '1.4' }}>
                      {isSuccess ? (
                        `Payment of amount Ksh ${parseFloat(item.amount).toFixed(2)} received for ${item.username} account number ${item.accountNumber} on ${item.dateTime}.`
                      ) : (
                        `Payment of amount Ksh ${parseFloat(item.amount).toFixed(2)} has been rejected. Account number ${item.accountNumber} does not exist.`
                      )}
                    </span>
                    <button 
                      onClick={() => handleDismissAlert(item.id)} 
                      style={styles.closeCardBtn}
                    >
                      &times;
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {renderContent()}
      </main>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#eef2f5',
    fontFamily: 'Inter, Arial, sans-serif',
  },
  header: {
    backgroundColor: '#007bff',
    color: 'white',
    padding: '20px 0',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
    marginBottom: '20px',
  },
  title: {
    textAlign: 'center',
    marginBottom: '15px',
    fontSize: '2.2em',
    fontWeight: '700',
  },
  loggedInAs: {
    textAlign: 'right',
    color: 'white',
    fontSize: '1.1em',
    paddingRight: '20px',
    margin: '0 0 10px 0',
  },
  nav: {
    display: 'flex',
    justifyContent: 'center',
    gap: '15px',
    padding: '0 20px',
    flexWrap: 'wrap',
  },
  navButton: {
    padding: '10px 15px',
    backgroundColor: 'transparent',
    color: 'white',
    border: '2px solid rgba(255, 255, 255, 0.5)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontWeight: '500',
    fontSize: '1em',
    textShadow: '1px 1px 2px rgba(0,0,0,0.1)',
    minWidth: '200px',
    textAlign: 'center',
  },
  activeButton: {
    padding: '10px 15px',
    backgroundColor: 'white',
    color: '#007bff',
    border: '2px solid white',
    borderRadius: '8px',
    cursor: 'default',
    fontWeight: '700',
    fontSize: '1em',
    boxShadow: '0 0 10px rgba(255, 255, 255, 0.5)',
    minWidth: '200px',
    textAlign: 'center',
  },
  mainContent: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  logoutButton: {
    padding: '10px 15px',
    backgroundColor: '#dc3545',
    color: 'white',
    border: '2px solid #dc3545',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: '700',
    fontSize: '1em',
    minWidth: '120px',
    textAlign: 'center',
    transition: 'all 0.3s ease',
  },
  /* --- CUSTOM STREAMING PANEL STYLES --- */
  alertPanelWrapper: {
    backgroundColor: '#ffffff',
    border: '1px solid #dcdfe3',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
  panelHeadingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #f1f3f5',
    paddingBottom: '8px',
    marginBottom: '12px',
  },
  clearAllTextBtn: {
    backgroundColor: 'transparent',
    color: '#007bff',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '12px',
    textDecoration: 'underline',
  },
  alertItemsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    maxHeight: '240px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  alertCard: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 14px',
    borderRadius: '4px',
  },
  closeCardBtn: {
    background: 'none',
    border: 'none',
    color: '#888',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '0 0 0 10px',
    lineHeight: '1',
    fontWeight: '700',
  }
};

export default AdminDashboard;